import { ApiOptions, Maybe, Patient, PatientMedicationsMeta, RestOptions, ZonedDateTime } from '@csp/csp-common-model';
import { getMeta } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { UserRestServiceV1 } from '@csp/dmdp-api-client';
import { PatientMedicationsMetaV1, UserMetaV1, UserV1 } from '@csp/dmdp-api-user-dto';
import { cloneDeep, uniq } from 'lodash';
import { RequestCode } from '@csp/csp-common-scheduling';
import { PatientMedicationMetaFactory } from '../model/PatientMedicationMetaFactory';

const META_KEY = 'PATIENT_MEDICATION_V1';

const addOrUpdateMeta = async (
  patient: Patient,
  patientMedications: PatientMedicationsMetaV1,
  apiOptions: RestOptions,
): Promise<void> => {
  await UserRestServiceV1.addOrUpdateMeta(apiOptions, patient.userV1, patientMedications);
};

const getMedication = (userV1: UserV1): PatientMedicationsMeta => {
  const metas = userV1.metas as Maybe<UserMetaV1[]>;
  const patientMedicationsV1 =
    getMeta(metas, META_KEY)?.data ?? PatientMedicationMetaFactory.emptyPatientMedicationsV1();

  return PatientMedicationMetaFactory.fromPatientMedicationsV1(patientMedicationsV1);
};

/** Clears all visits in the patient's meta */
const clearMedications = async (userId: string, apiOptions?: ApiOptions): Promise<void> => {
  try {
    await UserRestServiceV1.deleteMeta(toRestOptions(apiOptions), userId, META_KEY);
  } catch {
    // Do not fail on this
  }
};

const startMedication = async (
  patient: Patient,
  requestCode: RequestCode,
  startTimestamp: ZonedDateTime,
  apiOptions?: ApiOptions,
): Promise<PatientMedicationsMeta> => {
  const patientMedicationsMeta = cloneDeep(patient.medications);
  patientMedicationsMeta.requests[requestCode] = { startTimestamp }; // Intentional mutation
  const patientMedicationsMetaV1 = PatientMedicationMetaFactory.toPatientMedicationsMetaV1(patientMedicationsMeta);
  await addOrUpdateMeta(patient, patientMedicationsMetaV1, toRestOptions(apiOptions));
  return patientMedicationsMeta;
};

/**
 * Adds the passed in medication request code to the patient's meta data.
 * Typically called when pausing a medication.
 */
const pauseMedication = async (patient: Patient, requestCode: RequestCode, apiOptions?: ApiOptions): Promise<void> => {
  const patientMedicationsMeta = cloneDeep(patient.medications);
  const newPausedRequestCodes = uniq([...patientMedicationsMeta.pausedRequestCodes, requestCode]);
  patientMedicationsMeta.pausedRequestCodes = newPausedRequestCodes; // Intentional mutation
  const patientMedicationsMetaV1 = PatientMedicationMetaFactory.toPatientMedicationsMetaV1(patientMedicationsMeta);
  await addOrUpdateMeta(patient, patientMedicationsMetaV1, toRestOptions(apiOptions));
};

/**
 * Removes the passed in medication request code from the patient's meta data.
 * Typically called when stopping or continuing a medication.
 */
const unpauseMedication = async (
  patient: Patient,
  requestCode: RequestCode,
  apiOptions?: ApiOptions,
): Promise<void> => {
  const patientMedicationsMeta = cloneDeep(patient.medications);
  const newPausedRequestCodes = uniq(
    patientMedicationsMeta.pausedRequestCodes.filter(pausedRequestCode => pausedRequestCode !== requestCode),
  );
  patientMedicationsMeta.pausedRequestCodes = newPausedRequestCodes; // Intentional mutation
  const patientMedicationsMetaV1 = PatientMedicationMetaFactory.toPatientMedicationsMetaV1(patientMedicationsMeta);
  await addOrUpdateMeta(patient, patientMedicationsMetaV1, toRestOptions(apiOptions));
};

export const PatientMedicationMetaService = {
  startMedication,
  pauseMedication,
  unpauseMedication,
  getMedication,
  clearMedications,
};
